import { apiRequest } from '@/services/http'
import type {
  SiteAccessOptions,
  SiteAuthorOption,
  SiteAuthorCategoryOption,
  SiteAuthorCategoryQuota,
  SiteAuthorCreateRequest,
  SiteAuthorCreateResponse,
  SiteAuthorListItem,
  SiteAuthorListParams,
  SiteAuthorUpdateRequest,
  SiteCreateOptions,
  SiteCreateRequest,
  SiteCreateResponse,
  SiteDetail,
  SiteListItem,
  SiteListParams,
  SiteOwnerAdminUpdateRequest,
  SitePageResult,
  SitePreview,
  SitePreviewParams,
  SiteUpdateRequest,
  GoogleAuthenticatorStatus,
  GoogleAuthenticatorSetup
} from '@/types/countrySite'
import type {
  SiteDemoGameClassifyOption,
  SiteDemoGameDetail,
  SiteDemoGameRelatedOdds,
  SiteDemoGameListParams,
  SiteDemoGameMechanicsOption,
  SiteDemoGameAuthorOption,
  SiteDemoGameOptionPageParams,
  SiteDemoGameOptionPageResult,
  SiteDemoGamePageResult,
  SiteDemoGameProviderOption,
  SiteDemoGameSaveRequest
} from '@/types/countrySiteGame'
import type {
  SiteGameClassificationDetail,
  SiteGameClassificationListParams,
  SiteGameClassificationOptionPageParams,
  SiteGameClassificationOptionPageResult,
  SiteGameClassificationPageResult,
  SiteGameClassificationRouteOption,
  SiteGameClassificationUpdateRequest
} from '@/types/countrySiteGameClassification'
import type {
  SiteGameProviderDetail,
  SiteGameProviderListParams,
  SiteGameProviderPageResult,
  SiteGameProviderUpdateRequest,
  SiteGameProviderStatusRequest,
  SiteGameProviderStatusResponse
} from '@/types/countrySiteGameProvider'
import type {
  SiteDemoGameTopicDetail,
  SiteDemoGameTopicListParams,
  SiteDemoGameTopicPageResult,
  SiteDemoGameTopicUpdateRequest
} from '@/types/countrySiteGameTopic'
import type {
  SiteOddsDetail,
  SiteOddsEditOptions,
  SiteOddsLicenseOption,
  SiteOddsListParams,
  SiteOddsPageResult,
  SiteOddsSaveRequest,
  SiteOddsUserOption
} from '@/types/countrySiteOdds'
import type {
  SiteOddsActivityCreateResponse,
  SiteOddsActivityDetail,
  SiteOddsActivityListParams,
  SiteOddsActivityOption,
  SiteOddsActivityPageResult,
  SiteOddsActivitySaveRequest
} from '@/types/countrySiteOddsActivity'
import type {
  SiteCarouselCreateResponse,
  SiteCarouselItem,
  SiteCarouselListParams,
  SiteCarouselPageResult,
  SiteCarouselSaveRequest
} from '@/types/country-site-carousel'
const SITES_URL = '/admin/sites'
const SITE_URL = '/admin/site'
const SITE_CAROUSELS_URL = `${SITE_URL}/carousels`

export function getSiteCarouselPage(siteId: string, params: SiteCarouselListParams) {
  return apiRequest<SiteCarouselPageResult>({
    method: 'GET',
    url: SITE_CAROUSELS_URL,
    params: { ...params, siteId }
  })
}

export function getSiteCarouselDetail(siteId: string, postBannerId: string) {
  return apiRequest<SiteCarouselItem>({
    method: 'GET',
    url: `${SITE_CAROUSELS_URL}/${postBannerId}`,
    params: { siteId }
  })
}

export function createSiteCarousel(siteId: string, data: SiteCarouselSaveRequest) {
  return apiRequest<SiteCarouselCreateResponse>({
    method: 'POST',
    url: SITE_CAROUSELS_URL,
    params: { siteId },
    data
  })
}

export function updateSiteCarousel(siteId: string, postBannerId: string, data: SiteCarouselSaveRequest) {
  return apiRequest<void>({
    method: 'PUT',
    url: `${SITE_CAROUSELS_URL}/${postBannerId}`,
    params: { siteId },
    data
  })
}

export function deleteSiteCarousel(siteId: string, postBannerId: string) {
  return apiRequest<void>({
    method: 'DELETE',
    url: `${SITE_CAROUSELS_URL}/${postBannerId}`,
    params: { siteId }
  })
}

function withOptionalSiteId(siteId: string | null | undefined) {
  return siteId ? { siteId } : {}
}

export function getSiteAccessOptions() {
  return apiRequest<SiteAccessOptions>({ method: 'GET', url: `${SITES_URL}/access-options` })
}

export function getSitePage(params: SiteListParams) {
  return apiRequest<SitePageResult<SiteListItem>>({ method: 'GET', url: SITES_URL, params })
}

export function getSiteDetail(siteId: string) {
  return apiRequest<SiteDetail>({ method: 'GET', url: `${SITES_URL}/detail`, params: { siteId } })
}

export function getSiteCreateOptions() {
  return apiRequest<SiteCreateOptions>({ method: 'GET', url: `${SITES_URL}/create-options` })
}

export function previewSite(params: SitePreviewParams) {
  return apiRequest<SitePreview>({ method: 'GET', url: `${SITES_URL}/preview`, params })
}

export function createSite(data: SiteCreateRequest) {
  return apiRequest<SiteCreateResponse>({ method: 'POST', url: SITES_URL, data })
}

export function reimportSiteData(siteId: string) {
  return apiRequest<void>({ method: 'POST', url: `${SITES_URL}/reimport`, params: { siteId } })
}

export function updateSite(siteId: string, data: SiteUpdateRequest) {
  return apiRequest<void>({ method: 'PUT', url: SITES_URL, params: { siteId }, data })
}

export function updateSiteOwnerAdmins(siteId: string, data: SiteOwnerAdminUpdateRequest) {
  return apiRequest<void>({ method: 'PUT', url: `${SITES_URL}/owner-admins`, params: { siteId }, data })
}

export function getGoogleAuthenticatorStatus() {
  return apiRequest<GoogleAuthenticatorStatus>({
    method: 'GET',
    url: '/admin/center/auth/google-authenticator/status'
  })
}

export function setupGoogleAuthenticator() {
  return apiRequest<GoogleAuthenticatorSetup>({
    method: 'POST',
    url: '/admin/center/auth/google-authenticator/setup'
  })
}

export function confirmGoogleAuthenticator(code: string) {
  return apiRequest<void>({
    method: 'POST',
    url: '/admin/center/auth/google-authenticator/confirm',
    data: { code }
  })
}

export function updateSiteSeoBlocking(siteId: string, seoBlocked: boolean, googleAuthenticatorCode: string) {
  return apiRequest<void>({
    method: 'PUT',
    url: `${SITES_URL}/seo-blocking`,
    params: { siteId },
    data: { seoBlocked, googleAuthenticatorCode }
  })
}

const SITE_AUTHORS_URL = `${SITE_URL}/authors`

export function getSiteAuthorCategoryQuotas(siteId: string | null | undefined) {
  return apiRequest<SiteAuthorCategoryQuota[]>({
    method: 'GET',
    url: `${SITE_AUTHORS_URL}/category-quotas`,
    params: withOptionalSiteId(siteId)
  })
}

export function getSiteAuthorPage(siteId: string | null | undefined, params: SiteAuthorListParams) {
  return apiRequest<SitePageResult<SiteAuthorListItem>>({
    method: 'GET',
    url: SITE_AUTHORS_URL,
    params: { ...params, ...withOptionalSiteId(siteId) }
  })
}

export function getSiteAuthorCategoryOptions(siteId: string | null | undefined) {
  return apiRequest<SiteAuthorCategoryOption[]>({
    method: 'GET',
    url: `${SITE_AUTHORS_URL}/category-options`,
    params: withOptionalSiteId(siteId)
  })
}

export function createSiteAuthor(siteId: string | null | undefined, data: SiteAuthorCreateRequest) {
  return apiRequest<SiteAuthorCreateResponse>({ method: 'POST', url: SITE_AUTHORS_URL, params: withOptionalSiteId(siteId), data })
}

export function updateSiteAuthor(
  siteId: string | null | undefined,
  siteAuthorId: string,
  data: SiteAuthorUpdateRequest
) {
  return apiRequest<void>({
    method: 'PUT',
    url: `${SITE_AUTHORS_URL}/${siteAuthorId}`,
    params: withOptionalSiteId(siteId),
    data
  })
}

const SITE_DEMO_GAMES_URL = `${SITE_URL}/demo-games`

export function getSiteDemoGamePage(siteId: string | null | undefined, params: SiteDemoGameListParams) {
  return apiRequest<SiteDemoGamePageResult>({
    method: 'GET',
    url: SITE_DEMO_GAMES_URL,
    params: { ...params, ...withOptionalSiteId(siteId) }
  })
}

export function getSiteDemoGameOptionPage(siteId: string | null | undefined, params: SiteDemoGameOptionPageParams) {
  return apiRequest<SiteDemoGameOptionPageResult>({
    method: 'GET',
    url: `${SITE_DEMO_GAMES_URL}/options`,
    params: { ...params, ...withOptionalSiteId(siteId) }
  })
}

export function getSiteDemoGameDetailBySiteId(siteId: string | null | undefined, siteDemoGameId: string) {
  return apiRequest<SiteDemoGameDetail>({
    method: 'GET',
    url: SITE_DEMO_GAMES_URL + '/by-site-id/' + siteDemoGameId,
    params: withOptionalSiteId(siteId)
  })
}

export function getSiteDemoGameDetail(siteId: string | null | undefined, demoGameId: string) {
  return apiRequest<SiteDemoGameDetail>({
    method: 'GET',
    url: `${SITE_DEMO_GAMES_URL}/${demoGameId}`,
    params: withOptionalSiteId(siteId)
  })
}

/** 游戏评测只读预览：本站代理盘口随机最多五家，demoGameId 仍使用来源游戏 ID。 */
export function getSiteDemoGameRelatedOdds(siteId: string, demoGameId: string, limit = 5) {
  return apiRequest<SiteDemoGameRelatedOdds>({
    method: 'GET',
    url: `${SITE_DEMO_GAMES_URL}/${demoGameId}/related-odds`,
    params: { siteId, limit }
  })
}

export function updateSiteDemoGame(siteId: string | null | undefined, demoGameId: string, data: SiteDemoGameSaveRequest) {
  return apiRequest<void>({
    method: 'PUT',
    url: `${SITE_DEMO_GAMES_URL}/${demoGameId}`,
    params: withOptionalSiteId(siteId),
    data
  })
}

/** 全局新闻引用使用新入口；旧本站文章接口保持兼容。 */
export function updateSiteDemoGameWithNews(siteId: string, demoGameId: string, data: SiteDemoGameSaveRequest, expectedArticleId: string | null) {
  return apiRequest<void>({ method: 'PUT', url: `/admin/sites/${siteId}/news-articles/games/${demoGameId}`,
    data: { game: { ...data, articleId: null }, newsArticleId: data.articleId || null, expectedArticleId } })
}
export function getSiteGameDesignatedOddsOptions(siteId: string, keyword = '', pageNumber = 1) {
  return apiRequest<{ records: Array<{siteOddsId: string | number; name: string}>; total: number }>({
    method: 'GET', url: `${SITE_DEMO_GAMES_URL}/designated-odds/options`, params: { siteId, keyword, pageNumber, pageSize: 50 }
  })
}

export function getSiteNewsArticleOptions(siteId: string, keyword = '', pageNumber = 1) {
  return apiRequest<{ records: Array<{articleId: string; title: string; languageReady: boolean}>; total: number }>({
    method: 'GET', url: `/admin/sites/${siteId}/news-articles/options`, params: { keyword, pageNumber, pageSize: 30 }
  })
}

export function getSiteDemoGameProviderOptions(siteId: string | null | undefined) {
  return apiRequest<SiteDemoGameProviderOption[]>({
    method: 'GET',
    url: `${SITE_DEMO_GAMES_URL}/provider-options`,
    params: withOptionalSiteId(siteId)
  })
}

export function getSiteDemoGameClassifyOptions(siteId: string | null | undefined) {
  return apiRequest<SiteDemoGameClassifyOption[]>({
    method: 'GET',
    url: `${SITE_DEMO_GAMES_URL}/classify-options`,
    params: withOptionalSiteId(siteId)
  })
}

export function getSiteDemoGameMechanicsOptions(siteId: string | null | undefined) {
  return apiRequest<SiteDemoGameMechanicsOption[]>({
    method: 'GET',
    url: `${SITE_DEMO_GAMES_URL}/mechanics-options`,
    params: withOptionalSiteId(siteId)
  })
}

export function getSiteDemoGameAuthorOptions(siteId: string | null | undefined) {
  return apiRequest<SiteDemoGameAuthorOption[]>({
    method: 'GET',
    url: `${SITE_DEMO_GAMES_URL}/author-options`,
    params: withOptionalSiteId(siteId)
  })
}

const SITE_GAME_CLASSIFICATIONS_URL = `${SITE_URL}/game-classifications`

export function getSiteGameClassificationPage(siteId: string | null | undefined, params: SiteGameClassificationListParams) {
  return apiRequest<SiteGameClassificationPageResult>({
    method: 'GET',
    url: SITE_GAME_CLASSIFICATIONS_URL,
    params: { ...params, ...withOptionalSiteId(siteId) }
  })
}

export function getSiteGameClassificationOptionPage(
  siteId: string | null | undefined,
  params: SiteGameClassificationOptionPageParams
) {
  return apiRequest<SiteGameClassificationOptionPageResult>({
    method: 'GET',
    url: `${SITE_GAME_CLASSIFICATIONS_URL}/options`,
    params: { ...params, ...withOptionalSiteId(siteId) }
  })
}

export function getSiteGameClassificationDetail(siteId: string | null | undefined, siteGameClassifyId: string) {
  return apiRequest<SiteGameClassificationDetail>({
    method: 'GET',
    url: `${SITE_GAME_CLASSIFICATIONS_URL}/${siteGameClassifyId}`,
    params: withOptionalSiteId(siteId)
  })
}

export function getSiteGameClassificationRouteOptions(siteId: string | null | undefined, keyword?: string) {
  return apiRequest<SiteGameClassificationRouteOption[]>({
    method: 'GET',
    url: `${SITE_GAME_CLASSIFICATIONS_URL}/route-options`,
    params: { ...withOptionalSiteId(siteId), ...(keyword?.trim() ? { keyword: keyword.trim() } : {}) }
  })
}

export function updateSiteGameClassification(
  siteId: string | null | undefined,
  siteGameClassifyId: string,
  data: SiteGameClassificationUpdateRequest
) {
  return apiRequest<void>({
    method: 'PUT',
    url: `${SITE_GAME_CLASSIFICATIONS_URL}/${siteGameClassifyId}`,
    params: withOptionalSiteId(siteId),
    data
  })
}

const SITE_GAME_PROVIDERS_URL = `${SITE_URL}/game-providers`

export function getSiteGameProviderPage(
  siteId: string | null | undefined,
  params: SiteGameProviderListParams
) {
  return apiRequest<SiteGameProviderPageResult>({
    method: 'GET',
    url: SITE_GAME_PROVIDERS_URL,
    params: { ...params, ...withOptionalSiteId(siteId) }
  })
}

export function getSiteGameProviderDetail(
  siteId: string | null | undefined,
  siteGameProviderId: string
) {
  return apiRequest<SiteGameProviderDetail>({
    method: 'GET',
    url: `${SITE_GAME_PROVIDERS_URL}/${siteGameProviderId}`,
    params: withOptionalSiteId(siteId)
  })
}

export function updateSiteGameProvider(
  siteId: string | null | undefined,
  siteGameProviderId: string,
  data: SiteGameProviderUpdateRequest
) {
  return apiRequest<void>({
    method: 'PUT',
    url: `${SITE_GAME_PROVIDERS_URL}/${siteGameProviderId}`,
    params: withOptionalSiteId(siteId),
    data
  })
}

export function updateSiteGameProviderStatus(siteId: string, siteGameProviderId: string, data: SiteGameProviderStatusRequest) {
  return apiRequest<SiteGameProviderStatusResponse>({
    method: 'PUT',
    url: `${SITE_GAME_PROVIDERS_URL}/${siteGameProviderId}/status`,
    params: { siteId },
    data
  })
}

const SITE_DEMO_GAME_TOPICS_URL = `${SITE_URL}/demo-game-topics`

export function getSiteDemoGameTopicPage(siteId: string | null | undefined, params: SiteDemoGameTopicListParams) {
  return apiRequest<SiteDemoGameTopicPageResult>({
    method: 'GET',
    url: SITE_DEMO_GAME_TOPICS_URL,
    params: { ...params, ...withOptionalSiteId(siteId) }
  })
}

export function getSiteDemoGameTopicDetail(siteId: string | null | undefined, siteDemoGameTopicId: string) {
  return apiRequest<SiteDemoGameTopicDetail>({
    method: 'GET',
    url: `${SITE_DEMO_GAME_TOPICS_URL}/${siteDemoGameTopicId}`,
    params: withOptionalSiteId(siteId)
  })
}

export function updateSiteDemoGameTopic(
  siteId: string | null | undefined,
  siteDemoGameTopicId: string,
  data: SiteDemoGameTopicUpdateRequest
) {
  return apiRequest<void>({
    method: 'PUT',
    url: `${SITE_DEMO_GAME_TOPICS_URL}/${siteDemoGameTopicId}`,
    params: withOptionalSiteId(siteId),
    data
  })
}

const SITE_ODDS_URL = `${SITE_URL}/odds`

export function getSiteOddsPage(siteId: string | null | undefined, params: SiteOddsListParams) {
  return apiRequest<SiteOddsPageResult>({
    method: 'GET',
    url: SITE_ODDS_URL,
    params: { ...params, ...withOptionalSiteId(siteId) }
  })
}

export function getSiteOddsLicenseOptions(siteId: string | null | undefined) {
  return apiRequest<SiteOddsLicenseOption[]>({
    method: 'GET',
    url: `${SITE_ODDS_URL}/license-options`,
    params: withOptionalSiteId(siteId)
  })
}

export function getSiteOddsDetail(siteId: string | null | undefined, siteOddsId: string) {
  return apiRequest<SiteOddsDetail>({
    method: 'GET',
    url: `${SITE_ODDS_URL}/${siteOddsId}`,
    params: withOptionalSiteId(siteId)
  })
}

export function getSiteOddsEditOptions(siteId: string | null | undefined) {
  return apiRequest<SiteOddsEditOptions>({
    method: 'GET',
    url: `${SITE_ODDS_URL}/edit-options`,
    params: withOptionalSiteId(siteId)
  })
}

export function updateSiteOdds(siteId: string | null | undefined, siteOddsId: string, data: SiteOddsSaveRequest) {
  return apiRequest<void>({
    method: 'PUT',
    url: `${SITE_ODDS_URL}/${siteOddsId}`,
    params: withOptionalSiteId(siteId),
    data
  })
}

/** 后端尚无盘口代表专用选项接口，复用仅要求登录且按站点隔离的用户精简搜索。 */
export function searchSiteOddsUserOptions(siteId: string | null | undefined, keyword: string) {
  return apiRequest<SiteOddsUserOption[]>({
    method: 'GET',
    url: `${SITE_URL}/messages/user-options`,
    params: { ...withOptionalSiteId(siteId), keyword }
  })
}

export function getSiteOddsActivityPage(siteId: string | null | undefined, params: SiteOddsActivityListParams) {
  return apiRequest<SiteOddsActivityPageResult>({
    method: 'GET',
    url: `${SITE_URL}/odds-activities`,
    params: { ...params, ...withOptionalSiteId(siteId) }
  })
}

export function getSiteOddsActivityDetail(siteId: string | null | undefined, activityId: string) {
  return apiRequest<SiteOddsActivityDetail>({
    method: 'GET',
    url: `${SITE_URL}/odds-activities/${activityId}`,
    params: withOptionalSiteId(siteId)
  })
}

export function createSiteOddsActivity(siteId: string | null | undefined, siteOddsId: string, data: SiteOddsActivitySaveRequest) {
  return apiRequest<SiteOddsActivityCreateResponse>({
    method: 'POST',
    url: `${SITE_ODDS_URL}/${siteOddsId}/activities`,
    params: withOptionalSiteId(siteId),
    data
  })
}

export function updateSiteOddsActivity(siteId: string | null | undefined, activityId: string, data: SiteOddsActivitySaveRequest) {
  return apiRequest<void>({
    method: 'PUT',
    url: `${SITE_URL}/odds-activities/${activityId}`,
    params: withOptionalSiteId(siteId),
    data
  })
}

export function deleteSiteOddsActivity(siteId: string | null | undefined, activityId: string) {
  return apiRequest<void>({
    method: 'DELETE',
    url: `${SITE_URL}/odds-activities/${activityId}`,
    params: withOptionalSiteId(siteId)
  })
}

export function getSiteOddsActivityOptions(siteId: string | null | undefined) {
  return apiRequest<SiteOddsActivityOption[]>({
    method: 'GET',
    url: `${SITE_URL}/odds-activity-options`,
    params: withOptionalSiteId(siteId)
  })
}

export function getSiteAuthorOptions(siteId: string | null | undefined) {
  return apiRequest<SiteAuthorOption[]>({ method: 'GET', url: `${SITE_URL}/authors/options`, params: siteId ? { siteId } : {} })
}
